import React, { useEffect, useRef, useState } from "react";
import { Check, Loader2, RefreshCw, Sparkles, X } from "lucide-react";
import { getApiBaseUrl } from "../../config/api";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import "./CaptionAssist.css";

const API_URL = getApiBaseUrl();

const REFINE_CHIPS = [
  { action: "formalize", label: "Formalize" },
  { action: "casual", label: "Casual" },
  { action: "shorten", label: "Shorten" },
  { action: "elaborate", label: "Elaborate" },
];

const KIND_META = {
  post: {
    noun: "caption",
    previewTitle: "Suggested caption",
    emptyPlaceholder:
      "Describe what you want to post — for example, a thank-you after today’s workshop.",
    draftPlaceholder:
      "Tell AI how to change this caption, or leave this blank and use Polish.",
    writing: "Writing a caption…",
    polishing: "Polishing your caption…",
    aria: "AI caption help",
  },
  blog: {
    noun: "blog",
    previewTitle: "Suggested blog",
    emptyPlaceholder:
      "Describe the blog you want — for example, what you learned from this week’s lesson.",
    draftPlaceholder: "Tell AI how to change this blog, or leave this blank and use Polish.",
    writing: "Writing your blog…",
    polishing: "Polishing your blog…",
    aria: "AI blog help",
  },
  article: {
    noun: "article",
    previewTitle: "Suggested article",
    emptyPlaceholder:
      "Describe the article — for example, a short guide to starting a study group.",
    draftPlaceholder:
      "Tell AI how to change this article, or leave this blank and use Polish.",
    writing: "Writing your article…",
    polishing: "Polishing your article…",
    aria: "AI article help",
  },
  hiring: {
    noun: "job description",
    previewTitle: "Suggested job description",
    emptyPlaceholder:
      "Describe the role — for example, a part-time tutor for beginner Python.",
    draftPlaceholder:
      "Tell AI how to change this job description, or leave this blank and use Polish.",
    writing: "Writing the job description…",
    polishing: "Polishing the job description…",
    aria: "AI hiring help",
  },
  open_to_work: {
    noun: "open-to-work note",
    previewTitle: "Suggested open-to-work note",
    emptyPlaceholder:
      "Describe what you’re looking for — for example, a frontend internship and the skills you want to use.",
    draftPlaceholder:
      "Tell AI how to change this note, or leave this blank and use Polish.",
    writing: "Writing your open-to-work note…",
    polishing: "Polishing your open-to-work note…",
    aria: "AI open-to-work help",
  },
  reply: {
    noun: "reply",
    previewTitle: "Suggested reply",
    emptyPlaceholder: "Describe the reply you want — for example, agree and add one question.",
    draftPlaceholder: "Tell AI how to change this reply, or leave this blank and use Polish.",
    writing: "Writing a reply…",
    polishing: "Polishing your reply…",
    aria: "AI reply help",
  },
  question: {
    noun: "question details",
    previewTitle: "Suggested question details",
    emptyPlaceholder:
      "Describe what you want to ask — for example, how to prepare for a data analytics workshop.",
    draftPlaceholder:
      "Tell AI how to change these details, or leave this blank and use Polish.",
    writing: "Writing your question…",
    polishing: "Polishing your question…",
    aria: "AI question help",
  },
};

export function plainExcerpt(value, max = 500) {
  const plain = String(value || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();
  if (plain.length <= max) return plain;
  return `${plain.slice(0, max).trim()}…`;
}

function htmlToPlain(html) {
  return String(html || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|h1|h2|h3|h4|div|blockquote)>/gi, "\n\n")
    .replace(/<li[^>]*>/gi, "• ")
    .replace(/<\/li>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function suggestionToHtml(text) {
  const raw = String(text || "").trim();
  if (!raw) return "";
  if (/<\/?[a-z][\s\S]*>/i.test(raw)) return sanitizeHtml(raw);
  const blocks = raw.split(/\n{2,}/).map((block) => {
    const lines = block.split("\n");
    const bulletLines = lines.filter((line) => /^[•*-]\s+/.test(line.trim()));
    if (bulletLines.length === lines.length && lines.length > 0) {
      const items = lines
        .map((line) => `<li>${escapeHtml(line.replace(/^[•*-]\s+/, "").trim())}</li>`)
        .join("");
      return `<ul>${items}</ul>`;
    }
    return `<p>${escapeHtml(block).replace(/\n/g, "<br>")}</p>`;
  });
  return sanitizeHtml(blocks.join(""));
}

function CaptionAssist({
  text = "",
  title = "",
  category = "",
  context = "",
  kind = "post",
  format = "plain",
  userId,
  disabled = false,
  onApply,
  onApplyTitle,
  style,
}) {
  const meta = KIND_META[kind] || KIND_META.post;
  const rich = format === "html";
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("prompt");
  const [prompt, setPrompt] = useState("");
  const [suggestion, setSuggestion] = useState("");
  const [suggestionHtml, setSuggestionHtml] = useState("");
  const [suggestedTitle, setSuggestedTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const lastRequestRef = useRef(null);
  const abortRef = useRef(null);

  const plainDraft = plainExcerpt(text, 20000);
  const hasDraft = plainDraft.length > 0;

  useEffect(
    () => () => {
      if (abortRef.current) abortRef.current.abort();
    },
    []
  );

  const closePanel = () => {
    if (abortRef.current) abortRef.current.abort();
    setOpen(false);
    setMode("prompt");
    setPrompt("");
    setSuggestion("");
    setSuggestionHtml("");
    setSuggestedTitle("");
    setLoading(false);
    setError("");
    lastRequestRef.current = null;
  };

  const requestAssist = async ({ action, sourceText, extraPrompt }) => {
    if (!userId) {
      setError("Please sign in to use AI writing help.");
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    lastRequestRef.current = { action, sourceText, extraPrompt };

    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/polish_post.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          user_id: userId,
          action,
          kind,
          text: sourceText,
          title,
          category,
          context,
          prompt: extraPrompt || "",
        }),
      });
      const data = await res.json();
      if (!data?.success) {
        throw new Error(data?.message || `Could not write this ${meta.noun}.`);
      }
      const nextText = String(data.text || "").trim();
      if (rich) {
        setSuggestionHtml(nextText);
        setSuggestion(htmlToPlain(nextText) || nextText);
      } else {
        setSuggestionHtml("");
        setSuggestion(nextText);
      }
      setSuggestedTitle(String(data.title || "").trim());
      setMode("preview");
    } catch (err) {
      if (err?.name === "AbortError") return;
      setError(err.message || `Could not write this ${meta.noun} right now.`);
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
      }
    }
  };

  const openHelp = () => {
    setOpen(true);
    setMode("prompt");
    setError("");
    setSuggestion("");
    setSuggestionHtml("");
  };

  const polishNow = () => {
    setOpen(true);
    if (!hasDraft) {
      setMode("prompt");
      return;
    }
    requestAssist({
      action: "polish",
      sourceText: text,
      extraPrompt: "",
    });
  };

  const createFromPrompt = () => {
    const idea = prompt.trim();
    if (!idea && !hasDraft) {
      setError("Describe what you want to say.");
      return;
    }
    requestAssist({
      action: hasDraft && idea ? "custom" : idea ? "write" : "polish",
      sourceText: text,
      extraPrompt: idea,
    });
  };

  const insertSuggestion = () => {
    const next = suggestion.trim();
    if (!next) return;
    if (rich) {
      const originalPlain = htmlToPlain(suggestionHtml);
      const unchanged = suggestionHtml && next === originalPlain;
      onApply?.(suggestionToHtml(unchanged ? suggestionHtml : next));
    } else {
      onApply?.(next);
    }
    if (kind !== "reply" && !String(title || "").trim() && suggestedTitle) {
      onApplyTitle?.(suggestedTitle);
    }
    closePanel();
  };

  return (
    <div className="pf-ai" style={style}>
      {!open && (
        <div className="pf-ai-bar">
          <button
            type="button"
            className="pf-ai-chip pf-ai-chip--primary"
            disabled={disabled}
            onClick={openHelp}
          >
            <Sparkles size={14} className="pf-ai-sparkle" />
            Help me write
          </button>
          {hasDraft && (
            <button
              type="button"
              className="pf-ai-chip"
              disabled={disabled}
              onClick={polishNow}
            >
              <Sparkles size={14} className="pf-ai-sparkle" />
              Polish
            </button>
          )}
        </div>
      )}

      {open && (
        <div className="pf-ai-panel" role="region" aria-label={meta.aria}>
          <div className="pf-ai-panel-head">
            <h3 className="pf-ai-panel-title">
              <Sparkles size={15} className="pf-ai-sparkle" />
              {mode === "preview" ? meta.previewTitle : "Help me write"}
            </h3>
            <button
              type="button"
              className="pf-ai-iconbtn"
              aria-label="Close AI help"
              disabled={loading}
              onClick={closePanel}
            >
              <X size={14} strokeWidth={2.4} />
            </button>
          </div>

          {loading && (
            <p className="pf-ai-status">
              <Loader2 size={14} className="pf-ai-spin" />
              {hasDraft && !prompt.trim() ? meta.polishing : meta.writing}
            </p>
          )}

          {error && <p className="pf-ai-status pf-ai-status--error">{error}</p>}

          {mode === "prompt" && (
            <div className="pf-ai-prompt">
              <textarea
                className="pf-ai-input"
                value={prompt}
                disabled={loading || disabled}
                rows={kind === "reply" ? 2 : 3}
                placeholder={hasDraft ? meta.draftPlaceholder : meta.emptyPlaceholder}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    createFromPrompt();
                  }
                }}
              />
              <div className="pf-ai-actions">
                {hasDraft && (
                  <button
                    type="button"
                    className="pf-ai-btn pf-ai-btn--ghost"
                    disabled={loading || disabled}
                    onClick={polishNow}
                  >
                    Polish draft
                  </button>
                )}
                <button
                  type="button"
                  className="pf-ai-btn pf-ai-btn--primary"
                  disabled={loading || disabled}
                  onClick={createFromPrompt}
                >
                  {loading ? "Writing…" : "Create"}
                </button>
              </div>
            </div>
          )}

          {mode === "preview" && (
            <>
              <div className="pf-ai-prompt">
                <textarea
                  className="pf-ai-preview"
                  value={suggestion}
                  disabled={loading || disabled}
                  rows={kind === "reply" ? 3 : rich ? 8 : 4}
                  onChange={(e) => setSuggestion(e.target.value)}
                  placeholder={loading ? "Working on a suggestion…" : "Suggestion will appear here"}
                />
              </div>
              <div className="pf-ai-refines">
                {REFINE_CHIPS.map((chip) => (
                  <button
                    key={chip.action}
                    type="button"
                    className="pf-ai-refine"
                    disabled={loading || disabled || !suggestion.trim()}
                    onClick={() =>
                      requestAssist({
                        action: chip.action,
                        sourceText: suggestion || text,
                        extraPrompt: "",
                      })
                    }
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
              <div className="pf-ai-actions">
                <button
                  type="button"
                  className="pf-ai-btn pf-ai-btn--ghost"
                  disabled={loading || disabled}
                  onClick={closePanel}
                >
                  Discard
                </button>
                <button
                  type="button"
                  className="pf-ai-btn pf-ai-btn--ghost"
                  disabled={loading || disabled}
                  onClick={() => {
                    const last = lastRequestRef.current;
                    if (last) requestAssist(last);
                    else polishNow();
                  }}
                >
                  <RefreshCw size={13} />
                  Recreate
                </button>
                <button
                  type="button"
                  className="pf-ai-btn pf-ai-btn--primary"
                  disabled={loading || disabled || !suggestion.trim()}
                  onClick={insertSuggestion}
                >
                  <Check size={14} />
                  Insert
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default CaptionAssist;
